import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { colorManagerAtom, showStickyNavAtom } from '../../store/uiStore';
import MediaViewer from '../shared/MediaViewer';
import { Photo } from '../../interfaces/photos';
import clsx from 'clsx';
import { photoOrientation } from '@/utils/photoOrientation';
import { getMediaType, getOrientation } from '@/utils/mediaHelpers';
import { LINK_HEIGHT, LINK_ROW_SPAN } from '@/pages/collection/[id]';
import SocialLinkNote from '../moodboard/SocialLinkNote';
import AddNoteModal from '../moodboard/AddNoteModal';
import ModalContainer from '../shared/ModalContainer';
import useBoardDuplication from '@/hooks/useBoardDuplication';

export interface NoteState {
  [key: number]: { span: number; showNote: boolean };
}

const StandardPhotoGrid = ({
  media,
  gridItemWidth,
  gridItemHeight,
  gridGap,
  singleRow = false,
  showBookmarkButton = true,
  showQuickDownload = true,
  showBigDownloadButton = false,
  overlayHoverActive = false,
  limit = null,
  dashedPlaceholder = false,
  showEmptyState = true,
  lazyLoad = true,
  download = false,
  lastItemComponent,
  firstItemComponent,
  onEditLink,
  children,
  childrenRowNumber,
  defaultGridRows = 1,
  displayNote = false,
  showRankingScore = false,
  showTrendButton = false,
}: {
  media;
  gridItemWidth;
  gridItemHeight;
  gridGap;
  singleRow?: boolean;
  showBookmarkButton?: boolean;
  showQuickDownload?: boolean;
  showBigDownloadButton?: boolean;
  overlayHoverActive?: boolean;
  limit?: null | number;
  dashedPlaceholder?: boolean;
  showEmptyState?: boolean;
  lazyLoad?: boolean;
  download?: boolean;
  lastItemComponent?: React.ReactNode;
  firstItemComponent?: React.ReactNode;
  onEditLink?: (id: number) => void;
  children?: React.ReactNode;
  childrenRowNumber?: number;
  defaultGridRows?: number;
  displayNote?: boolean;
  showRankingScore?: boolean;
  showTrendButton?: boolean;
}) => {
  const [filteredMedia, setFilteredMedia] = useState(media);
  const [hover, setHover] = useState<{
    media: Photo | null;
    target: any | null;
    index: number | null;
    orientation: string | null;
  }>({
    media: null,
    target: null,
    index: null,
    orientation: null,
  });
  const [overlayLoaded, setOverlayLoaded] = useState(false);
  const [showStickyNav] = useAtom(showStickyNavAtom);
  const [lastPortraitIndex, setLastPortraitIndex] = useState<number>();
  const [colorManager] = useAtom(colorManagerAtom);
  const [addNoteBookmarkId, setAddNoteBookmarkId] = useState<number>(0);

  const gridAutoRows = useMemo(() => {
    if (children && childrenRowNumber !== undefined) {
      return `repeat(${childrenRowNumber - 1}, ${gridItemHeight}px) max-content`;
    } else {
      return singleRow ? '' : `${gridItemHeight}px`;
    }
  }, [children, childrenRowNumber, singleRow, gridItemHeight]);

  const gridAutoType = media.length > 2 ? 'auto-fit' : 'auto-fill';
  const gridLayout = {
    gridTemplateColumns:
      singleRow && limit
        ? 'repeat(' + limit + ', 1fr)'
        : `repeat(${gridAutoType}, minmax(${gridItemWidth}px, 1fr))`,
    gridAutoRows: gridAutoRows,
    gap: `${gridGap}px`,
    height: '',
    gridTemplateRows: '',
    overflow: '',
  };

  const mouseOverMedia = (media, target, index, orientation) => {
    setOverlayLoaded(false);
    setHover({ media, target, index, orientation });
  };

  useEffect(() => {
    const lastPortrait = filteredMedia
      .slice()
      .reverse()
      .findIndex(
        (img) =>
          img !== undefined &&
          (photoOrientation(img) === 'portrait' || photoOrientation(img) === 'square')
      );

    if (lastPortrait) {
      setLastPortraitIndex(filteredMedia.length - lastPortrait - 1);
    }
  }, [filteredMedia]);

  // for now just showing the first few photos
  useEffect(() => {
    if (!!media.length) {
      if (singleRow && limit) {
        let count = 0;
        // get only enough images to add up to limit grid spots including landscape span 2
        const images = media.filter((img) => {
          if (count === limit) return null;

          const orientation = photoOrientation(img);

          if (orientation === 'landscape' && count < limit - 1) {
            count += 2;
            return img;
          }

          if (orientation === 'portrait' && count < limit) {
            count += 1;
            return img;
          }
        });

        // if limit > images.length, add empty images to fill the rest
        const emptyFill = Array(limit - count).fill(null);
        const allImages = images.concat(emptyFill);
        setFilteredMedia(allImages);
      } else {
        if (media.length === 0) {
          const emptyFill = Array(12).fill(null);
          const allImages = media.concat(emptyFill);
          setFilteredMedia(allImages);
        } else {
          setFilteredMedia(media);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media, singleRow]);

  const [noteState, setNoteState] = useState<NoteState>({});

  const handleSeeNotesClick = (link) => {
    const isShowing = noteState[link.bookmarkId]?.showNote;
    const noteRowSpan = 1;
    if (isShowing) {
      setNoteState({ ...noteState, [link.bookmarkId]: { span: LINK_ROW_SPAN, showNote: false } });
    } else {
      setNoteState({
        ...noteState,
        [link.bookmarkId]: { span: LINK_ROW_SPAN + noteRowSpan, showNote: true },
      });
    }
  };

  useEffect(() => {
    if (displayNote) {
      const initialNoteState = media?.reduce((acc, cur) => {
        if (cur) {
          acc[cur.bookmarkId] = { span: LINK_ROW_SPAN, showNote: false };
        }
        return acc;
      }, {});
      setNoteState(initialNoteState);
    }
  }, [media, displayNote]);

  const { setShowOwnershipTipModal, isOwnerOfCollection } = useBoardDuplication();

  const handleAddNoteClick = (media) => {
    if (isOwnerOfCollection) {
      setAddNoteBookmarkId(media.bookmarkId);
    } else {
      setShowOwnershipTipModal(true);
    }
  };

  const setRowSpan = (link, noteRowSpan) => {
    setNoteState({
      ...noteState,
      [link.bookmarkId]: { span: LINK_ROW_SPAN + noteRowSpan, showNote: true },
    });
  };

  return (
    <>
      <div className="grid grid-flow-row-dense" style={gridLayout}>
        {firstItemComponent}
        {children}
        {filteredMedia.map((media, index) => {
          const mediaType = getMediaType(media);

          const orientation = getOrientation(media, mediaType);

          const gridStyle = {
            gridColumn: '',
            order: undefined,
            aspectRatio: '',
            gridRow: '',
          };
          if (orientation === 'landscape') {
            gridStyle.gridColumn = 'span 2';
          }
          if (
            (orientation === 'portrait' || orientation === 'square') &&
            index === lastPortraitIndex
          ) {
            // gridStyle.order = "2"; // @Jesh - I am unsure why you are doing this, have removed to fix single row with empty slots?
          }

          if (noteState[media?.bookmarkId]) {
            gridStyle.gridRow = `span ${noteState[media.bookmarkId].span}`;
          } else {
            gridStyle.gridRow = `span ${defaultGridRows}`;
          }

          return (
            <div
              style={gridStyle}
              key={media?.bookmarkId ?? `${media?.id}-${index}}`}
              onMouseEnter={(event) => {
                mouseOverMedia(media, event.target, index, null);
              }}
              onMouseLeave={() => {
                mouseOverMedia(null, null, null, null);
              }}
            >
              {media &&
                (media.imgix ||
                  mediaType === 'video' ||
                  mediaType === 'clip' ||
                  mediaType === 'link') && (
                  <MediaViewer
                    media={media}
                    showBookmarkButton={showBookmarkButton}
                    showQuickDownload={showQuickDownload}
                    showBigDownloadButton={showBigDownloadButton}
                    lazyLoad={lazyLoad}
                    download={download}
                    onEditLink={onEditLink}
                    onAddNote={handleAddNoteClick}
                    fixedHeightMedia={displayNote ? LINK_HEIGHT : 0}
                    noteState={noteState}
                    displayNote={displayNote}
                    showRankingScore={showRankingScore}
                    showTrendButton={showTrendButton}
                  />
                )}
              {media?.bookmarkId && !!noteState[media.bookmarkId] && (
                <SocialLinkNote
                  media={media}
                  orientation={orientation}
                  noteState={noteState}
                  handleSeeNotesClick={handleSeeNotesClick}
                  handleAddNoteClick={handleAddNoteClick}
                  setRowSpan={setRowSpan}
                />
              )}
              {!media && showEmptyState && (
                <div
                  className={clsx([
                    'w-full h-full',
                    !dashedPlaceholder
                      ? colorManager.text === 'text-black'
                        ? 'shadow-[inset_0px_0px_0px_1px_#000000]'
                        : 'shadow-[inset_0px_0px_0px_1px_#ffffff]'
                      : '',
                  ])}
                >
                  {dashedPlaceholder && (
                    <svg
                      className="w-full h-full"
                      preserveAspectRatio="none"
                      viewBox="0 0 133 129"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="0.5"
                        y="0.5"
                        width="131.333"
                        height="128"
                        rx="4"
                        stroke="white"
                        vectorEffect="non-scaling-stroke"
                        strokeDasharray="8 8"
                      />
                    </svg>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {lastItemComponent}
        {addNoteBookmarkId > 0 && (
          <ModalContainer
            className="border-0"
            onClose={() => {
              setAddNoteBookmarkId(0);
            }}
          >
            <AddNoteModal
              bookmarkId={addNoteBookmarkId}
              closeSelf={() => setAddNoteBookmarkId(0)}
              setNoteState={setNoteState}
              noteState={noteState}
            />
          </ModalContainer>
        )}
      </div>
      {overlayHoverActive && hover.media && hover.target && (
        <>
          <div
            className={clsx([
              'fixed z-50 top-0 left-0 w-full h-full flex items-center justify-center bg-black/0 pointer-events-none pt-[113px] transition-transform will-change-transform',
              showStickyNav ? '' : 'translate-y-[-56px] ',
            ])}
            onContextMenu={(e) => e.preventDefault()}
          >
            <div className="relative overflow-hidden">
              {/* eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text */}
              <img
                onLoad={() => setOverlayLoaded(true)}
                src={hover.media.imgix?.links['large_image_url']}
                className={clsx([
                  'absolute w-full h-full top-0 left-0 transition duration-[400ms] ease-out',
                  overlayLoaded ? 'scale-100' : 'scale-105',
                ])}
              />
              {/* eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text */}
              <img
                src={hover.media.imgix?.links['large_image_url']}
                className="max-w-[50vw] max-h-[70vh] opacity-0"
              />
            </div>
          </div>
          <div
            className="fixed border border-white z-50 w-10 h-10 pointer-events-none"
            style={{
              top: hover.target.getBoundingClientRect().top,
              left: hover.target.getBoundingClientRect().left,
              width: hover.target.getBoundingClientRect().width,
              height: hover.target.getBoundingClientRect().height,
            }}
          ></div>
        </>
      )}
    </>
  );
};

export default StandardPhotoGrid;
